import { ClientError, GraphQLClient } from 'graphql-request';
import { GraphQLClientResponse, Variables } from 'graphql-request/build/esm/types';

import { notifyToSentry } from 'src/features/shared/SentryErrorLogger';
import { environment } from 'src/environments';
import { fetchHasuraToken } from 'src/features/shared/queries/apis';

// Function to log errors to Sentry
function logErrorToSentry(response: Error | GraphQLClientResponse<unknown>) {
    if (!(response instanceof Error) && response.errors) {
        // type assertion is needed here as types are not defined properly in graphql-request
        const slackMessage = `URL: ${(response as unknown as ClientError).request.query}\nGraphQL Token: ${JSON.stringify(
            graphqlClientWithAuth.requestConfig.headers
        )}\nLocal Token: ${localStorage.getItem(environment.P)}\nError: ${JSON.stringify(response.errors)}`;
        notifyToSentry(slackMessage);
    }
}

export async function saveUpdatedHasuraTokenInLs() {
    const newToken = await fetchHasuraToken();
    localStorage.setItem(environment.P, newToken.data.token);
}

/**
 * Function to handle JWT token expiry
 * @param response - The response from the GraphQL client
 * @param requestCallback - The callback function to be executed after the token is refreshed,
 * type of callback is () => Promise<any> for now, it will be handled when type handling is done using codegen
 */
async function handleJwtTokenExpiry(response: GraphQLClientResponse<unknown>, requestCallback: () => Promise<any>) {
    if (response.errors && response.errors.some((err) => err.message.includes('JWTExpired'))) {
        await saveUpdatedHasuraTokenInLs();
        return requestCallback();
    }
}

const graphqlClientWithAuth = new GraphQLClient(environment.Q, {
    requestMiddleware: (request) => {
        const token = localStorage.getItem(environment.P);
        if (token) {
            request.headers = {
                ...request.headers,
                Authorization: `Bearer ${token}`
            };
        }
        return request;
    },
    responseMiddleware: logErrorToSentry
});

const graphqlClientWithoutAuth = new GraphQLClient(environment.Q, {
    responseMiddleware: logErrorToSentry
});

interface GraphqlRequestType {
    document: string;
    variables?: Variables;
}

async function graphqlRequest({ document, variables }: GraphqlRequestType, authRequest = true) {
    const client = authRequest ? graphqlClientWithAuth : graphqlClientWithoutAuth;
    try {
        return await client.request(document, variables);
    } catch (error: any) {
        const rerequestData = await handleJwtTokenExpiry(error.response as GraphQLClientResponse<unknown>, () => client.request(document, variables));
        if (rerequestData) {
            return rerequestData;
        }
        throw error;
    }
}

async function batchGraphqlRequest(queries: GraphqlRequestType[], authRequest = true) {
    const client = authRequest ? graphqlClientWithAuth : graphqlClientWithoutAuth;
    try {
        return await client.batchRequests(queries);
    } catch (error) {
        if (!(error instanceof Error)) {
            return await handleJwtTokenExpiry(error as GraphQLClientResponse<unknown>, () => client.batchRequests(queries));
        }
        throw error;
    }
}

export { graphqlRequest, batchGraphqlRequest };
